// Field checks for Settings → Social Channels. Each returns the problems keyed
// by form field; an empty object means the form may be saved.
//
// WhatsApp and Facebook mirror channel-gateway's DTOs (upsert-whatsapp-config,
// upsert-facebook-config) and its endpoint normaliser, so anything this lets
// through the server accepts. The rules are no stricter than the server's,
// except where the form already marks a field required (*): a stored config
// that saved before can always be saved again. Secrets are write-only — a
// stored one comes back masked and a blank field keeps it — so a secret is
// only required when nothing is stored yet.

import { isValidE164, E164_INVALID_MESSAGE } from '@/lib/phone';

export type FieldErrors = Record<string, string>;

const API_VERSION = /^v\d+\.\d+$/;
/** Meta's numeric ids (phone number id, page id): channel-gateway's /^\d{5,100}$/. */
const META_ID = /^\d{5,100}$/;
const META_TOKEN_MIN = 20;
/** channel-gateway's BLOCKED_HOST_PATTERNS (whatsapp.util.ts). */
const BLOCKED_HOSTS = [
  /^localhost$/i,
  /^127\./,
  /^0\./,
  /^10\./,
  /^192\.168\./,
  /^172\.(1[6-9]|2\d|3[01])\./,
  /^169\.254\./,
  /^\[?::1\]?$/,
  /^\[?fe80:/i,
  /^\[?fc00:/i,
  /^\[?fd[0-9a-f]{2}:/i,
  /\.internal$/i,
  /\.local$/i,
];

const required = (label: string) => `${label} is required`;

function tooLong(value: string, max: number, label: string): string | undefined {
  return value.trim().length > max ? `${label} must be ${max} characters or fewer` : undefined;
}

function set(errors: FieldErrors, field: string, message: string | undefined) {
  if (message) errors[field] = message;
}

/** Same checks as channel-gateway's normalizeApiEndpoint; blank means "use the system endpoint". */
export function apiEndpointError(raw: string): string | undefined {
  const value = raw.trim();
  if (!value) return undefined;
  if (value.length > 500) return 'Endpoint URL must be 500 characters or fewer';
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return 'Enter a full URL, e.g. https://graph.facebook.com';
  }
  if (url.protocol !== 'https:') return 'The endpoint must use https';
  if (url.username || url.password) return 'The endpoint must not contain a username or password';
  if (BLOCKED_HOSTS.some((p) => p.test(url.hostname))) return 'The endpoint may not point at an internal address';
  return undefined;
}

export interface WhatsAppFormValues {
  apiVersion: string;
  phoneNumberId: string;
  accessToken: string;
  webhookVerifyToken: string;
  businessAccountId: string;
  apiEndpoint: string;
}

export function validateWhatsApp(v: WhatsAppFormValues, hasStoredToken: boolean): FieldErrors {
  const e: FieldErrors = {};
  const version = v.apiVersion.trim();
  set(e, 'apiVersion', !version ? required('API version') : !API_VERSION.test(version) ? 'API version must look like v25.0' : undefined);
  const phoneId = v.phoneNumberId.trim();
  set(
    e,
    'phoneNumberId',
    !phoneId
      ? required('Phone Number ID')
      : !META_ID.test(phoneId)
        ? 'Phone Number ID is the numeric id Meta issues (at least 5 digits)'
        : undefined,
  );
  const token = v.accessToken.trim();
  set(
    e,
    'accessToken',
    !token && !hasStoredToken
      ? required('Access token')
      : token && token.length < META_TOKEN_MIN
        ? 'This looks too short to be a Meta access token'
        : undefined,
  );
  set(e, 'webhookVerifyToken', tooLong(v.webhookVerifyToken, 255, 'Webhook verify token'));
  set(e, 'businessAccountId', tooLong(v.businessAccountId, 100, 'Business Account ID'));
  set(e, 'apiEndpoint', apiEndpointError(v.apiEndpoint));
  return e;
}

export interface FacebookFormValues {
  pageId: string;
  pageName: string;
  pageAccessToken: string;
  appId: string;
  appSecret: string;
  webhookVerifyToken: string;
}

export function validateFacebook(v: FacebookFormValues, hasStoredToken: boolean): FieldErrors {
  const e: FieldErrors = {};
  const pageId = v.pageId.trim();
  set(
    e,
    'pageId',
    !pageId ? required('Page ID') : !META_ID.test(pageId) ? 'Page ID is the numeric id of your Page (at least 5 digits)' : undefined,
  );
  set(e, 'pageName', !v.pageName.trim() ? required('Page name') : tooLong(v.pageName, 255, 'Page name'));
  set(e, 'appId', !v.appId.trim() ? required('App ID') : tooLong(v.appId, 100, 'App ID'));
  const token = v.pageAccessToken.trim();
  set(
    e,
    'pageAccessToken',
    !token && !hasStoredToken
      ? required('Page access token')
      : token && token.length < META_TOKEN_MIN
        ? 'This looks too short to be a Meta access token'
        : undefined,
  );
  set(e, 'appSecret', tooLong(v.appSecret, 255, 'App secret'));
  set(e, 'webhookVerifyToken', tooLong(v.webhookVerifyToken, 255, 'Webhook verify token'));
  return e;
}

export interface TwitterFormValues {
  accountId: string;
  accountHandle: string;
  bearerToken: string;
  apiKey: string;
  apiSecret: string;
  accessToken: string;
  accessSecret: string;
}

/** The form marks every key required; they are only demanded when nothing is stored yet. */
export function validateTwitter(v: TwitterFormValues, isNew: boolean): FieldErrors {
  const e: FieldErrors = {};
  const accountId = v.accountId.trim();
  set(e, 'accountId', !accountId ? required('Account ID') : !/^\d+$/.test(accountId) ? 'Account ID must contain digits only' : undefined);
  const handle = v.accountHandle.trim();
  set(
    e,
    'accountHandle',
    !handle ? required('Account handle') : !/^@?\w{1,15}$/.test(handle) ? 'Handle is up to 15 letters, numbers or underscores' : undefined,
  );
  if (isNew) {
    const secrets: Array<[keyof TwitterFormValues, string]> = [
      ['bearerToken', 'Bearer token'],
      ['apiKey', 'API key'],
      ['apiSecret', 'API secret'],
      ['accessToken', 'Access token'],
      ['accessSecret', 'Access secret'],
    ];
    for (const [field, label] of secrets) set(e, field, !v[field].trim() ? required(label) : undefined);
  }
  return e;
}

export interface InstagramFormValues {
  instagramAccountId: string;
  pageId: string;
  username: string;
  accessToken: string;
}

export function validateInstagram(v: InstagramFormValues, isNew: boolean): FieldErrors {
  const e: FieldErrors = {};
  const accountId = v.instagramAccountId.trim();
  set(
    e,
    'instagramAccountId',
    !accountId ? required('Instagram Account ID') : !/^\d+$/.test(accountId) ? 'Instagram Account ID must contain digits only' : undefined,
  );
  const pageId = v.pageId.trim();
  set(e, 'pageId', !pageId ? required('Connected Page ID') : !/^\d+$/.test(pageId) ? 'Page ID must contain digits only' : undefined);
  const username = v.username.trim();
  set(
    e,
    'username',
    !username ? required('Username') : !/^@?[\w.]{1,30}$/.test(username) ? 'Username is up to 30 letters, numbers, dots or underscores' : undefined,
  );
  if (isNew) set(e, 'accessToken', !v.accessToken.trim() ? required('Access token') : undefined);
  return e;
}

export interface SmsFormValues {
  accountSid: string;
  authToken: string;
  phoneNumber: string;
  messagingServiceSid: string;
}

/**
 * Twilio credentials are never sent back, so they are only demanded for a new
 * config; when typed they must look like Twilio's. The number Twilio sends
 * from must be E.164.
 */
export function validateSms(v: SmsFormValues, isNew: boolean): FieldErrors {
  const e: FieldErrors = {};
  const sid = v.accountSid.trim();
  set(
    e,
    'accountSid',
    !sid && isNew ? required('Account SID') : sid && !/^AC[0-9a-fA-F]{32}$/.test(sid) ? 'Account SID is AC followed by 32 hex characters' : undefined,
  );
  set(e, 'authToken', !v.authToken.trim() && isNew ? required('Auth token') : undefined);
  const phone = v.phoneNumber.trim();
  set(e, 'phoneNumber', !phone ? required('Phone number') : !isValidE164(phone) ? E164_INVALID_MESSAGE : undefined);
  const msid = v.messagingServiceSid.trim();
  set(
    e,
    'messagingServiceSid',
    msid && !/^MG[0-9a-fA-F]{32}$/.test(msid) ? 'Messaging Service SID is MG followed by 32 hex characters' : undefined,
  );
  return e;
}

export const hasErrors = (errors: FieldErrors) => Object.keys(errors).length > 0;
